import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart, Filter, X, Wine, Star } from 'lucide-react';
import { mockWineProducts } from '../data/mockWineProducts';
import { mockExperiences } from '../data/mockExperiences';
import { WineProduct } from '../types/WineProduct';
export function AcquistaScreen() {
  const [selectedType, setSelectedType] = useState<string>('Tutti');
  const [selectedExperience, setSelectedExperience] = useState<string>('Tutte');
  const [showFilters, setShowFilters] = useState(false);
  const [cart, setCart] = useState<{
    [key: string]: number;
  }>({});
  const wineTypes = ['Tutti', 'Rosso', 'Bianco', 'Rosato', 'Spumante', 'Passito'];
  const experienceOptions = [{
    id: 'Tutte',
    name: 'Tutte le esperienze'
  }, ...mockExperiences.map(exp => ({
    id: exp.id,
    name: exp.name
  }))];
  // Filter wines
  const filteredWines = useMemo(() => {
    return mockWineProducts.filter(wine => {
      const typeMatch = selectedType === 'Tutti' || wine.type === selectedType;
      const expMatch = selectedExperience === 'Tutte' || wine.experienceIds.includes(selectedExperience);
      return typeMatch && expMatch && wine.inStock;
    });
  }, [selectedType, selectedExperience]);
  const addToCart = (wineId: string) => {
    setCart(prev => ({
      ...prev,
      [wineId]: (prev[wineId] || 0) + 1
    }));
  };
  const cartItemsCount = Object.values(cart).reduce((sum, qty) => sum + qty, 0);
  return <div className="h-full flex flex-col bg-gray-50" data-id="element-189">
      {/* Header with Cart */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex-shrink-0" data-id="element-190">
        <div className="flex items-center justify-between" data-id="element-191">
          <div data-id="element-192">
            <h1 className="text-lg font-semibold text-gray-900" data-id="element-193">
              Acquista Vini
            </h1>
            <p className="text-xs text-gray-500 mt-0.5" data-id="element-194">
              {filteredWines.length} vini disponibili
            </p>
          </div>
          <button className="relative p-2 rounded-lg transition-colors" style={{
          backgroundColor: cartItemsCount > 0 ? '#6C083B' : '#F3F4F6',
          color: cartItemsCount > 0 ? '#FFFFFF' : '#6B7280'
        }} data-id="element-195">
            <ShoppingCart size={20} strokeWidth={1.5} data-id="element-196" />
            {cartItemsCount > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center" style={{
            backgroundColor: '#FFFFFF',
            color: '#6C083B'
          }} data-id="element-197">
                {cartItemsCount}
              </span>}
          </button>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex-shrink-0" data-id="element-198">
        <div className="flex items-center gap-2 overflow-x-auto" data-id="element-199">
          <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border flex-shrink-0" style={{
          borderColor: showFilters ? '#6C083B' : '#E5E7EB',
          backgroundColor: showFilters ? '#FFF5F7' : '#FFFFFF',
          color: showFilters ? '#6C083B' : '#6B7280'
        }} data-id="element-200">
            <Filter size={14} strokeWidth={1.5} data-id="element-201" />
            <span className="text-xs font-medium" data-id="element-202">Filtri</span>
          </button>

          {/* Type filters */}
          {wineTypes.map(type => <button key={type} onClick={() => setSelectedType(type)} className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex-shrink-0" style={{
          backgroundColor: selectedType === type ? '#6C083B' : '#FFFFFF',
          color: selectedType === type ? '#FFFFFF' : '#6B7280',
          border: `1px solid ${selectedType === type ? '#6C083B' : '#E5E7EB'}`
        }} data-id="element-203">
              {type}
            </button>)}
        </div>
      </div>

      {/* Experience Filter Dropdown */}
      {showFilters && <motion.div className="bg-white border-b border-gray-200 px-4 py-3 flex-shrink-0" initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: 'auto',
      opacity: 1
    }} exit={{
      height: 0,
      opacity: 0
    }} data-id="element-204">
          <label className="text-xs font-medium text-gray-700 mb-2 block" data-id="element-205">
            Filtra per esperienza
          </label>
          <select value={selectedExperience} onChange={e => setSelectedExperience(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" data-id="element-206">
            {experienceOptions.map(exp => <option key={exp.id} value={exp.id} data-id="element-207">
                {exp.name}
              </option>)}
          </select>
        </motion.div>}

      {/* Wine Grid */}
      <div className="flex-1 overflow-y-auto px-4 py-4" data-id="element-208">
        <div className="grid grid-cols-1 gap-4 max-w-2xl mx-auto" data-id="element-209">
          {filteredWines.map((wine, index) => <motion.div key={wine.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.05
        }} data-id="element-210">
              <div className="p-4" data-id="element-211">
                <div className="flex gap-4" data-id="element-212">
                  {/* Wine Image Placeholder */}
                  <div className="w-20 h-20 rounded-lg flex items-center justify-center flex-shrink-0" style={{
                backgroundColor: '#FFF5F7'
              }} data-id="element-213">
                    <Wine size={32} style={{
                  color: '#6C083B'
                }} strokeWidth={1.5} data-id="element-214" />
                  </div>

                  {/* Wine Info */}
                  <div className="flex-1 min-w-0" data-id="element-215">
                    <div className="flex items-start justify-between gap-2 mb-1" data-id="element-216">
                      <h3 className="font-semibold text-gray-900 text-sm" data-id="element-217">
                        {wine.name}
                      </h3>
                      {wine.rating && <div className="flex items-center gap-1 flex-shrink-0" data-id="element-218">
                          <Star size={12} fill="#6C083B" style={{
                      color: '#6C083B'
                    }} data-id="element-219" />
                          <span className="text-xs font-medium text-gray-700" data-id="element-220">
                            {wine.rating}
                          </span>
                        </div>}
                    </div>

                    <p className="text-xs text-gray-600 mb-1" data-id="element-221">
                      {wine.producer}
                      {wine.year && ` • ${wine.year}`}
                    </p>

                    <div className="flex items-center gap-2 mb-2" data-id="element-222">
                      <span className="px-2 py-0.5 rounded text-xs font-medium" style={{
                    backgroundColor: '#FFF5F7',
                    color: '#6C083B'
                  }} data-id="element-223">
                        {wine.type}
                      </span>
                      <span className="text-xs text-gray-500" data-id="element-224">
                        {wine.region}
                      </span>
                    </div>

                    {wine.description && <p className="text-xs text-gray-600 line-clamp-2 mb-3" data-id="element-225">
                        {wine.description}
                      </p>}

                    {/* Price and Add to Cart */}
                    <div className="flex items-center justify-between gap-3" data-id="element-226">
                      <div data-id="element-227">
                        <span className="text-lg font-bold text-gray-900" data-id="element-228">
                          €{wine.price.toFixed(2)}
                        </span>
                        {wine.alcoholContent && <span className="text-xs text-gray-500 ml-2" data-id="element-229">
                            {wine.alcoholContent}
                          </span>}
                      </div>

                      <button onClick={() => addToCart(wine.id)} className="px-4 py-2 rounded-lg text-xs font-semibold transition-colors" style={{
                    backgroundColor: '#6C083B',
                    color: '#FFFFFF'
                  }} data-id="element-230">
                        {cart[wine.id] ? `In carrello (${cart[wine.id]})` : 'Aggiungi'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>)}
        </div>

        {filteredWines.length === 0 && <div className="text-center py-12" data-id="element-231">
            <Wine size={48} className="mx-auto mb-4 text-gray-300" data-id="element-232" />
            <p className="text-gray-500 text-sm" data-id="element-233">
              Nessun vino trovato con i filtri selezionati
            </p>
          </div>}
      </div>
    </div>;
}